import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, InputAdornment, Paper, Popover, TextField } from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useErrorHandler } from "../../../common/hooks";
import useDebouncedCallback from "../../../common/hooks/useDebouncedCallback";
import { VariableNames } from "../../Constants/VariableNames";
import { useDesignerMode } from "../../Contexts/DesignerModeContext";
import { useChildContext } from "../../Hooks/useChildContext";
import { getContainerClasses, getPortalParentAttributes, useComponentSelection } from "../../Hooks/useComponentSelection";
import useViewOnOpen from "../../Hooks/useViewOnOpen";
import { GlobalSearchModel } from "../../Models/GlobalSearchModel";
import { PopoverAnchor } from "../../Models/PopoverAnchor";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

// Popover positioning configuration
const ANCHOR_ORIGINS: Record<PopoverAnchor, { vertical: "top" | "bottom"; horizontal: "left" | "right" }> = {
  [PopoverAnchor.TopRight]: { vertical: "top", horizontal: "right" },
  [PopoverAnchor.TopLeft]: { vertical: "top", horizontal: "left" },
  [PopoverAnchor.BottomRight]: { vertical: "bottom", horizontal: "right" },
  [PopoverAnchor.BottomLeft]: { vertical: "bottom", horizontal: "left" },
};

const TRANSFORM_ORIGINS: Record<PopoverAnchor, { vertical: "top" | "bottom"; horizontal: "left" | "right" }> = {
  [PopoverAnchor.TopRight]: { vertical: "bottom", horizontal: "right" },
  [PopoverAnchor.TopLeft]: { vertical: "bottom", horizontal: "left" },
  [PopoverAnchor.BottomRight]: { vertical: "top", horizontal: "right" },
  [PopoverAnchor.BottomLeft]: { vertical: "top", horizontal: "left" },
};

const GlobalSearchComponent: React.FC<IComponentProps<GlobalSearchModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isMouseDownInPopover = useRef(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hasEverOpened, setHasEverOpened] = useState(false);

  // Get debounce delay from component props (will update when component updates)
  const debounceMs = component.DebunceMs ?? 300;

  // Debounced callback to update both the context variable and debounced state
  // This will recreate when debounceMs changes due to dependency array
  const debouncedUpdateContext = useDebouncedCallback((newValue: string) => {
    // Update debounced state for child context
    setDebouncedSearchValue(newValue);

    // Update component Value if provided
    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, newValue);
      } catch (error) {
        errorHandler(error);
      }
    }
  }, debounceMs);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // Update UI state immediately
    setSearchValue(newValue);
    // Debounce the context update to avoid excessive evaluations
    debouncedUpdateContext(newValue);

    // Keep popover open while typing
    setPopoverOpen(true);
  };

  const handleFocus = () => {
    setPopoverOpen(true); // Open popover when input is focused
    setHasEverOpened(true);
  };

  const handleBlur = () => {
    // Don't close if clicking inside the popover
    if (isMouseDownInPopover.current) {
      isMouseDownInPopover.current = false;
      return;
    }
    // Close popover only if there's no search text
    if (!searchValue || searchValue.trim().length === 0) {
      setPopoverOpen(false);
    }
  };

  const handleClear = () => {
    setSearchValue("");
    setDebouncedSearchValue("");
    setPopoverOpen(false);
    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, "");
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleClosePopover = () => {
    setPopoverOpen(false);
  };

  const componentLayoutSx = getComponentLayout(component);
  const popoverLayoutSx = getComponentLayout({ Layout: component.PopoverLayout } as any);

  useViewOnOpen(component, context, "GlobalSearchComponent");

  // Memoize variables object to prevent unnecessary context updates
  // Use debounced value so children don't re-render on every keystroke
  const childVariables = useMemo(
    () => ({
      [VariableNames.SearchText]: debouncedSearchValue,
    }),
    [debouncedSearchValue]
  );

  // Create child context with $searchText variable
  const childContext = useChildContext(context, childVariables);

  // Enable designer mode selection - mark as container so children can be added
  const outerSelectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true, // MUST be true so designer knows it accepts children
    isEmpty: !component.Children || component.Children.length === 0,
  });

  const designerMode = useDesignerMode();

  // Get the visual container CSS classes for the inner box where children render
  const innerContainerClassName = useMemo(() => {
    const isEmpty = !component.Children || component.Children.length === 0;
    return getContainerClasses(IsDesignMode, isEmpty);
  }, [component.Children]);

  // Handle clicks in designer mode for BOTH outer and inner containers
  const handleComponentClick = useCallback(
    (e: React.MouseEvent) => {
      if (!IsDesignMode) return;

      // Only handle Ctrl+Click or Cmd+Click (Mac)
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        designerMode?.onSelectComponent?.(component.Id, componentPath);
      }
    },
    [designerMode, component.Id, componentPath]
  );

  // Get anchor and transform origins from configuration
  const anchorOrigin = ANCHOR_ORIGINS[component.Anchor] ?? ANCHOR_ORIGINS[PopoverAnchor.BottomLeft];
  const transformOrigin = TRANSFORM_ORIGINS[component.Anchor] ?? TRANSFORM_ORIGINS[PopoverAnchor.BottomLeft];

  return (
    <Box
      ref={anchorRef}
      sx={{
        ...componentLayoutSx,
        overflow: "hidden", // Prevent horizontal scrollbar
      }}
      {...outerSelectionProps}
      // Override className to remove container styling from outer box
      className={outerSelectionProps.className
        ?.replace(/\biw-container\b/g, "")
        .replace(/\biw-empty\b/g, "")
        .trim()}
      onClick={handleComponentClick}
    >
      <TextField
        label={component.Label}
        placeholder={component.Placeholder}
        value={searchValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        fullWidth
        variant="standard"
        autoComplete="off"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} edge="end" disabled={!searchValue}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={handleClosePopover}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
        disableEnforceFocus // Always allow focus outside (so user can type in the TextField)
        disableAutoFocus // Don't steal focus from the TextField
        hideBackdrop={IsDesignMode} // No backdrop in designer mode to allow selecting other components
        disableScrollLock={IsDesignMode} // Allow page scrolling in designer mode
        keepMounted // Keep children mounted even when closed to preserve results
        slotProps={{
          root: {
            // Make the root non-modal in designer mode so clicks pass through
            style: IsDesignMode ? { pointerEvents: "none" } : undefined,
            onMouseDown: () => {
              isMouseDownInPopover.current = true;
            },
          },
          paper: {
            sx: {
              minWidth: Math.min(300, anchorRef.current?.offsetWidth || 300),
              maxWidth: 600,
              width: anchorRef.current?.offsetWidth || "auto",
              maxHeight: 500,
              overflow: "auto",
              // Re-enable pointer events on the paper
              pointerEvents: "auto",
              ...popoverLayoutSx,
            },
          },
        }}
      >
        <Paper ref={popoverRef} elevation={0} sx={{ position: "relative" }} {...getPortalParentAttributes(component.Id, componentPath)}>
          {/* Close button */}
          <IconButton
            size="small"
            onClick={handleClosePopover}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Render children components - apply container CSS classes here */}
          <Box
            className={innerContainerClassName}
            sx={{
              p: 2,
              pt: 5,
              minHeight: 100, // Ensure minimum height for empty placeholder
              // Ensure the container is clickable for selection in designer mode
              pointerEvents: "auto",
            }}
            onClick={handleComponentClick}
          >
            {hasEverOpened &&
              component.Children?.map((child) => (
                <ComponentFactory key={child.Id} parent={component} component={child} context={childContext} componentPath={[...componentPath, child.Id]} />
              ))}
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default GlobalSearchComponent;
